import React from 'react';
import {Image, View} from 'react-native';

import CCText from '../../../Atoms/CCText';
import {F, T} from '../courseTheme';

/**
 * A static picture of a chess position, for lists of positions.
 *
 * Not a chessboard component: a draggable board inside a scrolling list loses
 * its drag, and a dozen of them in one sheet is a lot of machinery for
 * something nobody plays. This is 64 squares and the piece images, with
 * nothing on it that can be pressed — so it is safe anywhere, scroll views
 * included.
 */

type Side = 'white' | 'black';

export type GridCell = {
  square: string;
  /** "wK", "bP"… or null for an empty square. */
  piece: string | null;
  dark: boolean;
};

const FILES = 'abcdefgh';

/**
 * The FEN's placement as eight rows of eight, top row first, the way the
 * board is seen from `orientation`'s side. Null when the placement is not
 * eight ranks of eight squares — drawing half a position would be worse than
 * saying it is unavailable.
 */
export function fenGrid(fen: string, orientation: Side): GridCell[][] | null {
  const placement = (fen || '').trim().split(/\s+/)[0] ?? '';
  const ranks = placement.split('/');
  if (ranks.length !== 8) return null;

  // board[r][f]: r = 0 is rank 8, f = 0 is the a-file.
  const board: (string | null)[][] = [];
  for (const rank of ranks) {
    const row: (string | null)[] = [];
    for (const ch of rank) {
      if (/[1-8]/.test(ch)) {
        for (let i = 0; i < Number(ch); i++) row.push(null);
      } else if (/[pnbrqkPNBRQK]/.test(ch)) {
        const white = ch === ch.toUpperCase();
        row.push(`${white ? 'w' : 'b'}${ch.toUpperCase()}`);
      } else {
        return null;
      }
    }
    if (row.length !== 8) return null;
    board.push(row);
  }

  const grid: GridCell[][] = [];
  for (let r = 0; r < 8; r++) {
    const row: GridCell[] = [];
    for (let f = 0; f < 8; f++) {
      // Seen from Black, both axes mirror.
      const boardRank = orientation === 'black' ? 7 - r : r;
      const boardFile = orientation === 'black' ? 7 - f : f;
      const rankNumber = 8 - boardRank;
      row.push({
        square: `${FILES[boardFile]}${rankNumber}`,
        piece: board[boardRank][boardFile],
        // a1 is dark: file index (a = 0) + rank number is odd on a dark square.
        dark: (boardFile + rankNumber) % 2 === 1,
      });
    }
    grid.push(row);
  }
  return grid;
}

// Static requires, so the bundler can see every asset. The app's own piece
// set (src/Assets/ChessPieces), as the Visualisation trainers use it.
const PIECE_FILES: Record<string, any> = {
  wK: require('../../../Assets/ChessPieces/White-King.svg'),
  wQ: require('../../../Assets/ChessPieces/White-Queen.svg'),
  wR: require('../../../Assets/ChessPieces/White-Rook.svg'),
  wB: require('../../../Assets/ChessPieces/White-Bishop.svg'),
  wN: require('../../../Assets/ChessPieces/White-Knight.svg'),
  wP: require('../../../Assets/ChessPieces/White-Pawn.svg'),
  bK: require('../../../Assets/ChessPieces/Black-King.svg'),
  bQ: require('../../../Assets/ChessPieces/Black-Queen.svg'),
  bR: require('../../../Assets/ChessPieces/Black-Rook.svg'),
  bB: require('../../../Assets/ChessPieces/Black-Bishop.svg'),
  bN: require('../../../Assets/ChessPieces/Black-Knight.svg'),
  bP: require('../../../Assets/ChessPieces/Black-Pawn.svg'),
};

/**
 * The bundler hands an SVG back as a URL string (or a module wrapping one);
 * Image wants `{uri}` for that, and a number only for a native asset.
 */
const PIECES: Record<string, any> = Object.fromEntries(
  Object.entries(PIECE_FILES).map(([key, file]) => {
    const value = file?.default ?? file;
    return [key, typeof value === 'string' ? {uri: value} : value];
  }),
);

/** The square colours every board in the app uses (FunChessboard et al.). */
export const LIGHT_SQUARE = '#EDD899';
export const DARK_SQUARE = '#D1AB41';

type Props = {
  fen: string;
  orientation: Side;
  size: number;
  /** What a screen reader hears for the whole board. */
  label: string;
  /** Left-out puzzles are drawn quieter, so the kept ones lead the eye. */
  muted?: boolean;
};

export default function MiniBoard({
  fen,
  orientation,
  size,
  label,
  muted = false,
}: Props) {
  const grid = fenGrid(fen, orientation);
  const cell = size / 8;

  if (!grid) {
    return (
      <View
        accessible
        accessibilityRole="image"
        accessibilityLabel={`${label}. Position unavailable.`}
        style={{
          width: size,
          height: size,
          borderRadius: 4,
          borderWidth: 1,
          borderColor: F.border10,
          backgroundColor: '#F1F0EF',
          alignItems: 'center',
          justifyContent: 'center',
          padding: 8,
        }}>
        <CCText
          style={{
            fontSize: T.xs,
            fontWeight: '600',
            color: F.mutedText,
            textAlign: 'center',
          }}>
          Position unavailable
        </CCText>
      </View>
    );
  }

  return (
    <View
      // One image to assistive tech: 64 squares read one by one is noise.
      accessible
      accessibilityRole="image"
      accessibilityLabel={label}
      style={{
        width: size,
        height: size,
        borderRadius: 4,
        overflow: 'hidden',
        // Non-text, so 0.55 is a de-emphasis, not a contrast failure.
        opacity: muted ? 0.55 : 1,
      }}>
      {grid.map((row, r) => (
        <View
          key={r}
          importantForAccessibility="no-hide-descendants"
          accessibilityElementsHidden
          style={{flexDirection: 'row', height: cell}}>
          {row.map(sq => (
            <View
              key={sq.square}
              style={{
                width: cell,
                height: cell,
                backgroundColor: sq.dark ? DARK_SQUARE : LIGHT_SQUARE,
              }}>
              {sq.piece ? (
                <Image
                  testID={`piece-${sq.square}`}
                  source={PIECES[sq.piece]}
                  style={{width: cell, height: cell}}
                  resizeMode="contain"
                />
              ) : null}
            </View>
          ))}
        </View>
      ))}
    </View>
  );
}
